import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { PublicClientApplication, EventType } from '@azure/msal-browser'
import type { EventMessage, AuthenticationResult } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { CoralogixRum } from '@coralogix/browser'
import { msalConfig } from './auth/authConfig'
import { ThemeProvider } from './contexts/ThemeContext'
import { isE2EMode } from './auth/e2eMode'
import { ErrorBoundary } from './components/ErrorBoundary'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import './index.css'
import App from './App.tsx'

// Only initialise RUM in deployed environments (staging/prod)
if (import.meta.env.VITE_CORALOGIX_ENABLED === 'true') {
  CoralogixRum.init({
    public_key: 'cxtp_08JEf881wiCzKVKduwuu5xJ0cXv3Co',
    application: 'feature-sentiment-console',
    version: __APP_VERSION__,
    coralogixDomain: 'EU2',
    environment: import.meta.env.VITE_CORALOGIX_ENVIRONMENT,
    sessionRecordingConfig: {
      enable: true,
      autoStartSessionRecording: true,
      recordConsoleEvents: true,
      sessionRecordingSampleRate: 100,
    },
    traceParentInHeader: {
      enabled: true,
      options: {
        // Propagate trace context to your API endpoints
        propagateTraceHeaderCorsUrls: [
          /https:\/\/api(-staging)?\.sentiment\.accessacloud\.com/,
        ],
      },
    },
  })
}

function setRumUserContext(account: { localAccountId: string; name?: string; username: string }) {
  if (import.meta.env.VITE_CORALOGIX_ENABLED !== 'true') return
  CoralogixRum.setUserContext({
    user_id: account.localAccountId,
    user_name: account.name ?? account.username,
    user_email: account.username,
  })
}

if (isE2EMode()) {
  // E2E test mode - ONLY enabled via VITE_E2E_MODE env var at build time
  // This code path is unreachable in production builds
  console.warn('⚠️ E2E TEST MODE ENABLED - Auth bypassed');
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
} else {
  // Normal MSAL authentication flow
  const msalInstance = new PublicClientApplication(msalConfig);

  // Initialize MSAL and wait for redirect handling before rendering
  msalInstance.initialize().then(async () => {
    // CRITICAL: Wait for redirect promise to complete before rendering
    // This prevents authentication loops
    try {
      const response = await msalInstance.handleRedirectPromise();
      if (response) {
        msalInstance.setActiveAccount(response.account);
        setRumUserContext(response.account);
      } else {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
          setRumUserContext(accounts[0]);
        }
      }
    } catch (error) {
      console.error("Error handling redirect:", error);
      if (import.meta.env.VITE_CORALOGIX_ENABLED === 'true' && error instanceof Error) {
        CoralogixRum.error(error.message, error);
      }
    }

    // Set up event callback for future login events
    msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        msalInstance.setActiveAccount(account);
        setRumUserContext(account);
      }
    });

    // Only render after redirect handling is complete
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <ErrorBoundary>
          <MsalProvider instance={msalInstance}>
            <ThemeProvider>
              <App />
            </ThemeProvider>
          </MsalProvider>
        </ErrorBoundary>
      </StrictMode>,
    );
  });
}
